import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  TikTokAdsConnectApi,
  TikTokAdsAdvertiser,
  TikTokAdsConnectionStatus,
  TikTokAdsDeliveryPreviewResult,
  TikTokAdsEntityPreviewResult,
  TikTokAdsRuntimeStatus,
  TikTokAdsSyncResult,
} from '../types';

export type TikTokAdsRowSelection = Record<string, boolean>;
export type TikTokAdsAction =
  | 'connect'
  | 'disconnect'
  | 'discover'
  | 'save'
  | 'entity-preview'
  | 'delivery-preview'
  | 'sync'
  | null;

function selectedIds(selection: TikTokAdsRowSelection): string[] {
  return Object.entries(selection)
    .filter(([, selected]) => selected)
    .map(([advertiserId]) => advertiserId)
    .sort();
}

function sameIds(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const normalizedLeft = [...left].sort();
  const normalizedRight = [...right].sort();
  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
}

function selectionFromAdvertisers(advertisers: TikTokAdsAdvertiser[]): TikTokAdsRowSelection {
  return Object.fromEntries(
    advertisers.filter((advertiser) => advertiser.selected).map((advertiser) => [advertiser.advertiserId, true]),
  );
}

function isoDateOffset(days: number): string {
  const value = new Date();
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

function createIdempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.()
    || `tiktok-sync-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useTikTokAdsConnect(
  projectId: string,
  service: TikTokAdsConnectApi,
) {
  const [runtime, setRuntime] = useState<TikTokAdsRuntimeStatus | null>(null);
  const [connections, setConnections] = useState<TikTokAdsConnectionStatus[]>([]);
  const [activeConnectionId, setActiveConnectionId] = useState('');
  const [advertisers, setAdvertisers] = useState<TikTokAdsAdvertiser[]>([]);
  const [rowSelection, setRowSelection] = useState<TikTokAdsRowSelection>({});
  const [persistedAdvertiserIds, setPersistedAdvertiserIds] = useState<string[]>([]);
  const [previewAdvertiserId, setPreviewAdvertiserId] = useState('');
  const [entityPreview, setEntityPreview] = useState<TikTokAdsEntityPreviewResult | null>(null);
  const [deliveryPreview, setDeliveryPreview] = useState<TikTokAdsDeliveryPreviewResult | null>(null);
  const [syncResult, setSyncResult] = useState<TikTokAdsSyncResult | null>(null);
  const [since, setSince] = useState(() => isoDateOffset(-6));
  const [until, setUntil] = useState(() => isoDateOffset(0));
  const [loading, setLoading] = useState(true);
  const [advertisersLoading, setAdvertisersLoading] = useState(false);
  const [action, setAction] = useState<TikTokAdsAction>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const actionRef = useRef<TikTokAdsAction>(null);
  const connectionRequest = useRef(0);
  const advertiserRequest = useRef(0);
  const previewRequest = useRef(0);
  const syncRequest = useRef(0);
  const connectionAbort = useRef<AbortController | null>(null);
  const advertiserAbort = useRef<AbortController | null>(null);
  const previewAbort = useRef<AbortController | null>(null);

  const activeConnection = useMemo(
    () => connections.find((connection) => connection.connectionId === activeConnectionId) || null,
    [activeConnectionId, connections],
  );
  const selectedAdvertiserIds = useMemo(() => selectedIds(rowSelection), [rowSelection]);
  const isDirty = useMemo(
    () => !sameIds(selectedAdvertiserIds, persistedAdvertiserIds),
    [persistedAdvertiserIds, selectedAdvertiserIds],
  );

  const beginAction = useCallback((next: Exclude<TikTokAdsAction, null>): boolean => {
    if (actionRef.current !== null) return false;
    actionRef.current = next;
    setAction(next);
    setError(null);
    setNotice(null);
    return true;
  }, []);

  const endAction = useCallback(() => {
    actionRef.current = null;
    setAction(null);
  }, []);

  const loadConnections = useCallback(async (showLoading = true): Promise<TikTokAdsConnectionStatus[]> => {
    connectionAbort.current?.abort();
    const controller = new AbortController();
    connectionAbort.current = controller;
    const sequence = ++connectionRequest.current;
    if (showLoading) setLoading(true);
    try {
      const response = await service.listConnections(projectId, controller.signal);
      if (controller.signal.aborted || sequence !== connectionRequest.current) return [];
      setConnections(response.connections);
      setRuntime(response.runtime ?? null);
      setActiveConnectionId((current) => {
        if (response.connections.some((connection) => connection.connectionId === current)) return current;
        return response.connections.find((connection) => connection.state === 'connected')?.connectionId
          || response.connections[0]?.connectionId
          || '';
      });
      return response.connections;
    } catch (loadError) {
      if (controller.signal.aborted || sequence !== connectionRequest.current) return [];
      setError(loadError instanceof Error ? loadError.message : 'Failed to load TikTok Ads connections');
      return [];
    } finally {
      if (showLoading && sequence === connectionRequest.current) setLoading(false);
    }
  }, [projectId, service]);

  const loadAdvertisers = useCallback(async (connection: TikTokAdsConnectionStatus): Promise<TikTokAdsAdvertiser[]> => {
    advertiserAbort.current?.abort();
    const controller = new AbortController();
    advertiserAbort.current = controller;
    const sequence = ++advertiserRequest.current;
    setAdvertisersLoading(true);
    try {
      const response = await service.discoverAdvertisers(projectId, connection, controller.signal);
      if (controller.signal.aborted || sequence !== advertiserRequest.current) return [];
      const persisted = response.advertisers
        .filter((advertiser) => advertiser.selected)
        .map((advertiser) => advertiser.advertiserId)
        .sort();
      setAdvertisers(response.advertisers);
      setRowSelection(selectionFromAdvertisers(response.advertisers));
      setPersistedAdvertiserIds(persisted);
      setPreviewAdvertiserId((current) => (
        persisted.includes(current) ? current : persisted[0] || ''
      ));
      if (response.warnings?.length) setNotice(response.warnings.join(' '));
      return response.advertisers;
    } catch (loadError) {
      if (controller.signal.aborted || sequence !== advertiserRequest.current) return [];
      setAdvertisers([]);
      setRowSelection({});
      setPersistedAdvertiserIds([]);
      setPreviewAdvertiserId('');
      setError(loadError instanceof Error ? loadError.message : 'Failed to discover TikTok Ads advertisers');
      return [];
    } finally {
      if (sequence === advertiserRequest.current) setAdvertisersLoading(false);
    }
  }, [projectId, service]);

  useEffect(() => {
    void loadConnections();
    return () => {
      connectionAbort.current?.abort();
      advertiserAbort.current?.abort();
      previewAbort.current?.abort();
      syncRequest.current += 1;
    };
  }, [loadConnections]);

  useEffect(() => {
    if (!activeConnection || activeConnection.state !== 'connected') {
      advertiserAbort.current?.abort();
      setAdvertisers([]);
      setRowSelection({});
      setPersistedAdvertiserIds(activeConnection?.selectedAdvertiserIds ?? []);
      setPreviewAdvertiserId('');
      return;
    }
    void loadAdvertisers(activeConnection);
  }, [activeConnection, loadAdvertisers]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('provider') !== 'tiktok_ads') return;
    if (params.get('status') === 'connected') {
      setNotice('TikTok Ads connected. Choose the advertisers Segmently may read.');
      void loadConnections();
    } else if (params.get('status') === 'error') {
      setError('TikTok Ads authorization was not completed.');
    }
  }, [loadConnections]);

  const chooseConnection = useCallback((connectionId: string) => {
    advertiserAbort.current?.abort();
    previewAbort.current?.abort();
    syncRequest.current += 1;
    setAdvertisers([]);
    setRowSelection({});
    setPersistedAdvertiserIds([]);
    setPreviewAdvertiserId('');
    setEntityPreview(null);
    setDeliveryPreview(null);
    setSyncResult(null);
    setError(null);
    setNotice(null);
    setActiveConnectionId(connectionId);
  }, []);

  const connect = useCallback(async () => {
    if (!runtime || !beginAction('connect')) return;
    try {
      if (runtime.apiEnvironment === 'sandbox') {
        await service.connectSandbox(projectId, 'sandbox');
        await loadConnections(false);
        setNotice('TikTok Ads sandbox connected. Select the advertiser Segmently may read.');
      } else {
        const url = await service.getOAuthUrl(projectId, activeConnection?.connectionId || 'default');
        window.location.assign(url);
      }
    } catch (connectError) {
      setError(connectError instanceof Error ? connectError.message : 'Failed to connect TikTok Ads');
    } finally {
      endAction();
    }
  }, [activeConnection, beginAction, endAction, loadConnections, projectId, runtime, service]);

  const disconnect = useCallback(async () => {
    if (!activeConnection || !beginAction('disconnect')) return;
    try {
      await service.disconnect(projectId, activeConnection.connectionId);
      advertiserAbort.current?.abort();
      previewAbort.current?.abort();
      syncRequest.current += 1;
      setEntityPreview(null);
      setDeliveryPreview(null);
      setSyncResult(null);
      await loadConnections(false);
      setNotice('TikTok Ads disconnected from this project.');
    } catch (disconnectError) {
      setError(disconnectError instanceof Error ? disconnectError.message : 'Failed to disconnect TikTok Ads');
    } finally {
      endAction();
    }
  }, [activeConnection, beginAction, endAction, loadConnections, projectId, service]);

  const refreshAdvertisers = useCallback(async () => {
    if (!activeConnection || isDirty || !beginAction('discover')) return;
    try {
      await loadAdvertisers(activeConnection);
    } finally {
      endAction();
    }
  }, [activeConnection, beginAction, endAction, isDirty, loadAdvertisers]);

  const discardSelection = useCallback(() => {
    setRowSelection(Object.fromEntries(persistedAdvertiserIds.map((advertiserId) => [advertiserId, true])));
    setError(null);
    setNotice(null);
  }, [persistedAdvertiserIds]);

  const saveSelection = useCallback(async () => {
    if (!activeConnection || selectedAdvertiserIds.length < 1 || selectedAdvertiserIds.length > 100
      || !beginAction('save')) return;
    try {
      const result = await service.selectAdvertisers({
        projectId,
        connectionId: activeConnection.connectionId,
        advertiserIds: selectedAdvertiserIds,
      });
      const readbackConnections = await service.listConnections(projectId);
      const readbackConnection = readbackConnections.connections.find(
        (connection) => connection.connectionId === activeConnection.connectionId,
      );
      if (!readbackConnection || !sameIds(result.selectedAdvertiserIds, selectedAdvertiserIds)
        || !sameIds(readbackConnection.selectedAdvertiserIds, selectedAdvertiserIds)) {
        throw new Error('TikTok Ads selection readback did not match the saved advertisers. Please retry.');
      }
      const readbackAdvertisers = await service.discoverAdvertisers(projectId, readbackConnection);
      const selectedFromAdvertisers = readbackAdvertisers.advertisers
        .filter((advertiser) => advertiser.selected)
        .map((advertiser) => advertiser.advertiserId);
      if (!sameIds(selectedFromAdvertisers, selectedAdvertiserIds)) {
        throw new Error('TikTok Ads selection readback did not match the saved advertisers. Please retry.');
      }
      setConnections(readbackConnections.connections);
      setRuntime(readbackConnections.runtime ?? null);
      setAdvertisers(readbackAdvertisers.advertisers);
      setRowSelection(selectionFromAdvertisers(readbackAdvertisers.advertisers));
      setPersistedAdvertiserIds([...selectedAdvertiserIds]);
      setPreviewAdvertiserId((current) => selectedAdvertiserIds.includes(current) ? current : selectedAdvertiserIds[0] || '');
      setEntityPreview(null);
      setDeliveryPreview(null);
      setSyncResult(null);
      setNotice(`Saved ${selectedAdvertiserIds.length} TikTok Ads advertiser${selectedAdvertiserIds.length === 1 ? '' : 's'}.`);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save TikTok Ads advertiser selection');
    } finally {
      endAction();
    }
  }, [activeConnection, beginAction, endAction, projectId, selectedAdvertiserIds, service]);

  const previewEntities = useCallback(async () => {
    if (!activeConnection || !previewAdvertiserId || isDirty || !beginAction('entity-preview')) return;
    previewAbort.current?.abort();
    const controller = new AbortController();
    previewAbort.current = controller;
    const sequence = ++previewRequest.current;
    try {
      const result = await service.previewEntities({
        projectId,
        connectionId: activeConnection.connectionId,
        advertiserId: previewAdvertiserId,
        signal: controller.signal,
      });
      if (controller.signal.aborted || sequence !== previewRequest.current) return;
      setEntityPreview(result);
      setSyncResult(null);
      setNotice(result.entities.length === 0
        ? 'No TikTok Ads entities were observed for this advertiser.'
        : 'TikTok Ads entity preview completed.');
    } catch (previewError) {
      if (!controller.signal.aborted && sequence === previewRequest.current) {
        setError(previewError instanceof Error ? previewError.message : 'Failed to preview TikTok Ads entities');
      }
    } finally {
      if (sequence === previewRequest.current) endAction();
    }
  }, [activeConnection, beginAction, endAction, isDirty, previewAdvertiserId, projectId, service]);

  const previewDelivery = useCallback(async () => {
    if (!activeConnection || !previewAdvertiserId || isDirty || !beginAction('delivery-preview')) return;
    previewAbort.current?.abort();
    const controller = new AbortController();
    previewAbort.current = controller;
    const sequence = ++previewRequest.current;
    try {
      const result = await service.previewDelivery({
        projectId,
        connectionId: activeConnection.connectionId,
        advertiserId: previewAdvertiserId,
        since,
        until,
        signal: controller.signal,
      });
      if (controller.signal.aborted || sequence !== previewRequest.current) return;
      setDeliveryPreview(result);
      setSyncResult(null);
      setNotice(result.rows.length === 0
        ? 'No TikTok Ads delivery rows were observed in this date window.'
        : 'TikTok Ads delivery preview completed.');
      await loadConnections(false);
    } catch (previewError) {
      if (!controller.signal.aborted && sequence === previewRequest.current) {
        setError(previewError instanceof Error ? previewError.message : 'Failed to preview TikTok Ads delivery');
      }
    } finally {
      if (sequence === previewRequest.current) endAction();
    }
  }, [activeConnection, beginAction, endAction, isDirty, loadConnections, previewAdvertiserId, projectId, service, since, until]);

  const sync = useCallback(async () => {
    if (!activeConnection || !previewAdvertiserId || !entityPreview || !deliveryPreview
      || isDirty || !beginAction('sync')) return;
    const sequence = ++syncRequest.current;
    try {
      const idempotencyKey = createIdempotencyKey();
      let result: TikTokAdsSyncResult | null = null;
      for (let attempt = 0; attempt < 20; attempt += 1) {
        result = await service.manualSync({
          projectId,
          connectionId: activeConnection.connectionId,
          advertiserId: previewAdvertiserId,
          reportDate: until,
          idempotencyKey,
        });
        if (sequence !== syncRequest.current) return;
        if (result.state === 'complete' || result.state === 'deduplicated') break;
        await new Promise((resolve) => setTimeout(resolve, 1_000));
        if (sequence !== syncRequest.current) return;
      }
      if (!result) throw new Error('TikTok Ads synchronization returned no result');
      setSyncResult(result);
      await loadConnections(false);
      setNotice(result.state === 'complete' || result.state === 'deduplicated'
        ? 'TikTok Ads reporting data is synchronized.'
        : 'TikTok Ads synchronization was accepted and is still processing. Refresh to check progress.');
    } catch (syncError) {
      setError(syncError instanceof Error ? syncError.message : 'Failed to synchronize TikTok Ads reporting data');
    } finally {
      endAction();
    }
  }, [activeConnection, beginAction, deliveryPreview, endAction, entityPreview, isDirty, loadConnections, previewAdvertiserId, projectId, service, until]);

  return {
    runtime,
    connections,
    activeConnection,
    activeConnectionId,
    advertisers,
    rowSelection,
    persistedAdvertiserIds,
    selectedAdvertiserIds,
    previewAdvertiserId,
    entityPreview,
    deliveryPreview,
    syncResult,
    since,
    until,
    loading,
    advertisersLoading,
    action,
    error,
    notice,
    isDirty,
    setRowSelection,
    setPreviewAdvertiserId,
    setSince,
    setUntil,
    clearError: () => setError(null),
    clearNotice: () => setNotice(null),
    chooseConnection,
    connect,
    disconnect,
    refreshConnections: () => loadConnections(),
    refreshAdvertisers,
    discardSelection,
    saveSelection,
    previewEntities,
    previewDelivery,
    sync,
  };
}
